import { useNavigate } from 'react-router'
import { observer, useLocalObservable } from 'mobx-react-lite'
import React, { useState } from 'react'
import { AxiosError } from 'axios'
import { Button, Divider, Icon, Tooltip, Typography } from 'shared/ui'
import { LINK_10DLC_LIMITS_EXCEEDED } from 'shared/constants/links.ts'
import { logger } from 'shared/lib'
import { DropdownCards, DropdownCardsStore } from 'entities/Billing/ui/DropdownCards'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { BillingApi } from 'entities/Billing'
import { ComplianceApi } from 'entities/Compliance'
import { StepsCommonLayout } from 'pages/settings/ui/StepsCommonLayout/StepsCommonLayout.tsx'
import { CompliancePath } from 'pages/settings/pages/compliance/route/compliancePath.ts'
import { SettingsPath } from 'pages/settings/route/settingsPath.ts'
import styles from './styles.module.scss'

export const Summary = observer(() => {
  const navigate = useNavigate()
  const { billingStore, subscriptionStore, complianceStore } = useEntitiesContext()
  const dropdownCardsStore = useLocalObservable(
    () =>
      new DropdownCardsStore(billingStore, subscriptionStore, {
        labelText: 'Payment method',
      })
  )
  const [loading, setLoading] = useState(false)
  const onUpgrade = async () => {
    try {
      setLoading(true)
      if (!dropdownCardsStore.activeId) return
      const { data } = await BillingApi.billingDepositReplenish({
        payment_method: dropdownCardsStore.activeId,
        price_id: 'a2p_brand_registration_fee_standard',
        type: 'price',
      })
      if (data) {
        const response = await ComplianceApi.upgradeBrand()
        await complianceStore.fetchComplianceServicesList()
        response && navigate(`/${SettingsPath.compliance.localNumbers.root}`)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.errors.getError('payment_method')) {
          dropdownCardsStore.setPaymentError('Please select a different card')
        }
        if (e.response?.data?.error_message?.includes('Brand must be on LOW_VOLUME_STANDARD')) {
          await complianceStore.fetchComplianceServicesList()
          navigate(`/${SettingsPath.compliance.localNumbers.root}`)
        }
      }
      logger.error(e)
    } finally {
      setLoading(false)
    }
  }
  return (
    <StepsCommonLayout
      progressTitle={'Order summary'}
      title={'Upgrade 10DLC package'}
      totalSteps={2}
      settingsStep={2}
      closeSetting={() => navigate('/' + CompliancePath.compliance.localNumbers.root)}
      decreaseStep={() => navigate(-1)}
      content={
        <>
          <div className={styles.content}>
            <div>
              <Typography ariaLabel={'desc'} variant={'body-md-regular'} color={'tertiary'}>
                These fees are set by the carrier to register your brand and use case. Salesmsg
                doesn&#39;t mark them up.&nbsp;
              </Typography>

              <Typography ariaLabel={'learnMore'} variant={'body-md-medium'} linkLight>
                <a href={LINK_10DLC_LIMITS_EXCEEDED} target='_blank' rel='noreferrer'>
                  Learn more
                </a>
              </Typography>
            </div>
            <div className={styles.summary}>
              <div className={styles.row}>
                <div className={styles.left}>
                  <Typography ariaLabel={'title'} variant={'body-md-regular'}>
                    Brand vetting fee (one-time)
                  </Typography>
                  <Tooltip
                    label={
                      'This is a one-time carrier fee for submitting it for the second vetting'
                    }
                    placement={'top'}
                    breakWord
                  >
                    <Icon
                      padding={2}
                      fontSize={12}
                      icon={'questionFull'}
                      color={'var(--content-neutral-primary)'}
                    />
                  </Tooltip>
                </div>

                <Typography ariaLabel={'value'} variant={'body-md-regular'}>
                  $61.00
                </Typography>
              </div>

              <Divider sx={{ borderColor: 'var(--black-20)' }} />
              <div className={styles.rowTitle}>
                <div className={styles.left}>
                  <Typography ariaLabel={'title'} variant={'body-lg-medium'}>
                    Total
                  </Typography>
                </div>

                <Typography ariaLabel={'value'} variant={'body-lg-medium'}>
                  $61.00
                </Typography>
              </div>
            </div>
            <DropdownCards store={dropdownCardsStore} />
          </div>
          <div className={styles.actions}>
            <Button
              text={' Upgrade package'}
              onClick={onUpgrade}
              disabled={!dropdownCardsStore.activeId}
              size={'large'}
              fullWidth
              loading={loading}
            />
          </div>
        </>
      }
    />
  )
})
